import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  CircularProgress,
  Typography,
  Button,
  Box,
  Link,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  Page,
  Content,
  MarkdownContent,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';

interface DocHeading {
  id: string;
  level: number;
  text: string;
}

const uploadedDocMetadataLinePrefixes: string[] = ['By user:', 'Updated '];
const dropLeadingHeadingMatchingTitle = true;

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const extractHeadings = (markdown: string): DocHeading[] => {
  const lines = markdown.split('\n');
  const seen = new Map<string, number>();
  const headings: DocHeading[] = [];

  lines.forEach(line => {
    const match = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
    if (!match) return;

    const level = match[1].length;
    const rawText = match[2].replace(/`/g, '').trim();
    if (!rawText) return;

    const base = slugify(rawText);
    if (!base) return;

    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;

    headings.push({ id, level, text: rawText });
  });

  return headings;
};

const normalizeMarkdownForDisplay = (markdown: string, docTitle: string): string => {
  const normalizedNewlines = markdown.replace(/\r\n/g, '\n');
  const lines = normalizedNewlines.split('\n');
  const filteredLines: string[] = [];
  let removedLeadingTitle = false;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (uploadedDocMetadataLinePrefixes.some((prefix: string) => trimmedLine.startsWith(prefix))) {
      continue;
    }

    if (dropLeadingHeadingMatchingTitle && !removedLeadingTitle) {
      const headingMatch = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
      const normalizedTitle = docTitle.trim().toLowerCase();
      if (headingMatch && headingMatch[2].trim().toLowerCase() === normalizedTitle) {
        removedLeadingTitle = true;
        continue;
      }
    }

    filteredLines.push(line);
  }

  return filteredLines
    .join('\n')
    .replace(/^(#{1,6}\s+.+?)\s+\{#[^}]+\}\s*$/gm, '$1')
    .replace(/^\s+/, '');
};

const useStyles = makeStyles(theme => ({
  headerBox: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: '20px',
    background: theme.palette.background.default,
  },
  headerContent: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '20px 24px 10px',
  },
  backButton: {
    marginBottom: '10px',
    color: theme.palette.primary.main,
    '&:hover': {
      background: 'transparent',
      textDecoration: 'underline',
    },
  },
  breadcrumb: {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  breadcrumbLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  titleSection: {
    marginBottom: '14px',
  },
  titleContent: {
    flex: 1,
  },
  title: {
    fontWeight: 600,
    fontSize: '32px',
    lineHeight: '1.3',
    margin: '0 0 6px 0',
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: '15px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    rowGap: '8px',
    columnGap: '12px',
    paddingTop: '4px',
    maxWidth: '680px',
  },
  detailLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  detailValue: {
    fontSize: '14px',
    color: theme.palette.text.primary,
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
  },
  docsLayout: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '0 24px 24px',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 260px',
    gap: '24px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  articleCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    minWidth: 0,
  },
  tocCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    padding: '16px 14px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    position: 'sticky' as const,
    top: '24px',
    alignSelf: 'start',
    [theme.breakpoints.down('sm')]: {
      position: 'static' as const,
      order: -1,
    },
  },
  tocTitle: {
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    margin: '0 0 10px',
    fontWeight: 700,
  },
  tocList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: '6px',
  },
  tocItem: {
    margin: 0,
  },
  tocLink: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '13px',
    lineHeight: 1.4,
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  markdownContent: {
    fontFamily: "'Arial', sans-serif",
    fontSize: '15px',
    lineHeight: '1.7',
    color: theme.palette.text.primary,
    '& > h1:first-child': {
      marginTop: 0,
    },
    '& h1': {
      fontFamily: "'Arial', sans-serif",
      fontSize: '32px',
      fontWeight: 700,
      marginTop: '32px',
      marginBottom: '16px',
      lineHeight: 1.3,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: '12px',
    },
    '& h2': {
      fontFamily: "'Arial', sans-serif",
      fontSize: '24px',
      fontWeight: 600,
      marginTop: '28px',
      marginBottom: '12px',
      lineHeight: 1.3,
      color: theme.palette.text.primary,
    },
    '& h3': {
      fontFamily: "'Arial', sans-serif",
      fontSize: '18px',
      fontWeight: 600,
      marginTop: '20px',
      marginBottom: '10px',
      lineHeight: 1.3,
    },
    '& h4, & h5, & h6': {
      fontFamily: "'Arial', sans-serif",
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '8px',
    },
    '& p': {
      margin: '12px 0',
      lineHeight: 1.7,
    },
    '& ul, & ol': {
      marginLeft: '24px',
      marginTop: '12px',
      marginBottom: '12px',
    },
    '& li': {
      marginBottom: '6px',
      lineHeight: 1.6,
    },
    '& code': {
      background: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
      borderRadius: '3px',
      padding: '2px 6px',
      fontSize: '13px',
      fontFamily: "'Courier New', monospace",
      color: theme.palette.type === 'dark' ? '#d4d4d4' : '#d73a49',
    },
    '& pre': {
      background: theme.palette.type === 'dark' ? '#1e1e1e' : '#f6f8fa',
      borderRadius: '6px',
      padding: '16px',
      overflowX: 'auto' as const,
      border: `1px solid ${theme.palette.divider}`,
      marginTop: '12px',
      marginBottom: '12px',
      '& code': {
        background: 'transparent',
        color: theme.palette.type === 'dark' ? '#d4d4d4' : '#24292e',
        padding: 0,
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: '16px',
      marginLeft: 0,
      marginTop: '12px',
      marginBottom: '12px',
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginTop: '12px',
      marginBottom: '12px',
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '12px',
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 600,
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '4px',
      marginTop: '12px',
      marginBottom: '12px',
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: '24px 0',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  errorContainer: {
    padding: '24px',
    background: theme.palette.background.paper,
    borderRadius: '6px',
  },
}));

export const DocsInstanceReaderPage = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);

  const [docName, setDocName] = useState<string>('');
  const [content, setContent] = useState<string | null>(null);
  const [headings, setHeadings] = useState<DocHeading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docMeta, setDocMeta] = useState<{ description: string; uploadedBy: string; uploadedAt: string } | null>(null);

  useEffect(() => {
    const loadDoc = async () => {
      try {
        if (!id) {
          setError('Document ID not found');
          setLoading(false);
          return;
        }

        const baseUrl = await discoveryApi.getBaseUrl('persistence');
        const { token } = await identityApi.getCredentials();
        const baseUrlStr = baseUrl.toString();
        
        const fetchHeaders: Record<string, string> = {};
        if (token) {
          fetchHeaders.Authorization = `Bearer ${token}`;
        }

        // Try to fetch artifact metadata first
        let artifactMeta: any = null;
        try {
          const metaResp = await fetch(`${baseUrlStr}/artifacts`, {
            headers: fetchHeaders,
          });
          if (metaResp.ok) {
            const data = await metaResp.json();
            const artifacts = data.artifacts ?? data ?? [];
            artifactMeta = artifacts.find((a: any) => a.id === id);
          }
        } catch (e) {
          // Continue without metadata
        }

        // Fetch document content
        const contentResp = await fetch(`${baseUrlStr}/artifacts/${id}/download`, {
          headers: fetchHeaders,
        });

        if (!contentResp.ok) {
          throw new Error(`Failed to load document: ${contentResp.statusText}`);
        }

        const text = await contentResp.text();
        const cleanedContent = normalizeMarkdownForDisplay(text, artifactMeta?.name || `Document-${id.substring(0, 8)}`);
        const parsedHeadings = extractHeadings(cleanedContent);

        setDocName(artifactMeta?.name || `Document-${id.substring(0, 8)}`);
        setDocMeta(
          artifactMeta
            ? {
                description: artifactMeta.description || '',
                uploadedBy: artifactMeta.uploaded_by || 'Unknown',
                uploadedAt: artifactMeta.uploaded_at ? new Date(artifactMeta.uploaded_at).toLocaleDateString() : '',
              }
            : null
        );
          setContent(cleanedContent);
          setHeadings(parsedHeadings);
        setError(null);
      } catch (err: any) {
        const errorMsg = err.message || 'Unknown error occurred';
        setError(errorMsg);
        alertApi.post({ message: `Could not load document: ${errorMsg}`, severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadDoc();
  }, [id, discoveryApi, identityApi, alertApi]);

  return (
    <Page themeId="documentation">
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Content>
          <Box style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 24px 0' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              className={classes.backButton}
              onClick={() => navigate('/docs')}
            >
              Back to Docs
            </Button>
            <Box style={{ marginTop: '24px', padding: '24px', background: 'rgba(211, 47, 47, 0.1)', borderRadius: '6px' }}>
              <Typography variant="h6" color="error">
                Error Loading Document
              </Typography>
              <Typography color="textSecondary" style={{ marginTop: '8px' }}>
                {error}
              </Typography>
            </Box>
          </Box>
        </Content>
      ) : (
        <>
          {/* Header Section */}
          <Box className={classes.headerBox}>
            <Box className={classes.headerContent}>
              <Button
                startIcon={<ArrowBackIcon />}
                className={classes.backButton}
                onClick={() => navigate('/docs')}
              >
                Back to Docs
              </Button>

              <div className={classes.breadcrumb}>
                <Link
                  component="button"
                  type="button"
                  className={classes.breadcrumbLink}
                  onClick={() => navigate('/docs')}
                >
                  Documentation
                </Link>
                <span>/</span>
                <span>{docName || 'Document'}</span>
              </div>

              <Box className={classes.titleSection}>
                <Box className={classes.titleContent}>
                  <h1 className={classes.title}>{docName || 'Document'}</h1>
                  {docMeta?.description && (
                    <p className={classes.subtitle}>{docMeta.description}</p>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Content Section */}
          <Content>
            <Box className={classes.docsLayout}>
              <Box className={classes.articleCard}>
                {content !== null ? (
                  <div className={classes.markdownContent}>
                    <MarkdownContent content={content} />
                  </div>
                ) : (
                  <Typography color="textSecondary">No content available.</Typography>
                )}
              </Box>

              <Box className={classes.tocCard}>
                <p className={classes.tocTitle}>On this page</p>
                {headings.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No section headings found.
                  </Typography>
                ) : (
                  <ul className={classes.tocList}>
                    {headings
                      .filter(h => h.level <= 3)
                      .map(h => (
                        <li
                          key={h.id}
                          className={classes.tocItem}
                          style={{ paddingLeft: `${Math.max(0, (h.level - 1) * 10)}px` }}
                        >
                          <a className={classes.tocLink} href={`#${h.id}`}>
                            {h.text}
                          </a>
                        </li>
                      ))}
                  </ul>
                )}
              </Box>
            </Box>
          </Content>
        </>
      )}
    </Page>
  );
};
